import { useState, useEffect, useMemo, useCallback } from "react";
import { ArrowLeft, Loader2, ChevronDown, Trash2 } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { supabase } from "../lib/supabase";
import { useCountryTranslations } from "../contexts/CountryTranslationsContext";
import { useToast } from "../contexts/ToastContext";
import type { Screen } from "../types";
import type { Database } from "../lib/database.types";

interface AdminScreenProps {
  onNavigate: (screen: Screen) => void;
}

interface MatchForAdmin {
  id: string;
  home_country_code: string;
  away_country_code: string;
  home_flag: string;
  away_flag: string;
  match_time: string;
  status: "upcoming" | "live" | "completed";
  home_score: number | null;
  away_score: number | null;
}

type ProfileRow = Database["public"]["Tables"]["profiles"]["Row"];
type SquadRow = Database["public"]["Tables"]["squads"]["Row"];
type Language = "en" | "fr" | "ar";
type TabType = "matches" | "users" | "squads";

interface UserForAdmin extends ProfileRow {
  squad: SquadRow | null;
  country_flag: string | null;
}

interface SquadForAdmin extends SquadRow {
  member_count: number;
  creator_name: string | null;
  creator_email: string | null;
}

interface CreatorInfo {
  name: string;
  email: string;
}

// Helper function to format dates based on language
const formatDate = (date: string, language: Language): string => {
  const localeMap: Record<Language, string> = {
    ar: "ar-EG",
    fr: "fr-FR",
    en: "en-US",
  };

  return new Date(date).toLocaleDateString(localeMap[language], {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export function AdminScreen({ onNavigate }: AdminScreenProps) {
  const { language, t } = useLanguage();
  const { getCountryName, translations } = useCountryTranslations();
  const [matches, setMatches] = useState<MatchForAdmin[]>([]);
  const [originalMatches, setOriginalMatches] = useState<MatchForAdmin[]>([]); // Track original state from DB
  const [users, setUsers] = useState<UserForAdmin[]>([]);
  const [squads, setSquads] = useState<SquadForAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);
  const [squadsLoading, setSquadsLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [deletingSquad, setDeletingSquad] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>("matches");
  const { showToast } = useToast();

  const fetchMatches = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("matches")
        .select("*")
        .order("match_time", { ascending: true });

      if (error) throw error;

      if (data) {
        const matchData = data as MatchForAdmin[];
        setMatches(matchData);
        setOriginalMatches(matchData);
      }
    } catch (error) {
      console.error("Failed to fetch matches:", error);
      showToast(
        `${t("common.error")}: ${
          error instanceof Error ? error.message : "Failed to fetch matches"
        }`,
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [showToast, t]);

  useEffect(() => {
    fetchMatches();
  }, [fetchMatches]);

  const fetchUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      // Fetch all profiles
      const { data: profilesData, error: profilesError } = await supabase
        .from("profiles")
        .select("*")
        .order("created_at", { ascending: false });

      if (profilesError) throw profilesError;
      if (!profilesData) return;

      const typedProfilesData = profilesData as ProfileRow[];

      // Fetch all squads for users who have current_squad_id
      const squadIds = [
        ...new Set(
          typedProfilesData
            .map((p) => p.current_squad_id)
            .filter((id): id is string => id !== null)
        ),
      ];

      const squadsMap = new Map<string, SquadRow>();
      if (squadIds.length > 0) {
        const { data: squadsData, error: squadsError } = await supabase
          .from("squads")
          .select("*")
          .in("id", squadIds);

        if (!squadsError && squadsData) {
          (squadsData as SquadRow[]).forEach((squad) => {
            squadsMap.set(squad.id, squad);
          });
        }
      }

      // Map profiles with squad info
      const usersWithFlags: UserForAdmin[] = typedProfilesData.map(
        (profile) => {
          const squad =
            profile.current_squad_id && squadsMap.has(profile.current_squad_id)
              ? squadsMap.get(profile.current_squad_id)!
              : null;

          return {
            ...profile,
            squad,
            country_flag: null,
          };
        }
      );

      setUsers(usersWithFlags);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      showToast(
        `${t("common.error")}: ${
          error instanceof Error ? error.message : "Failed to fetch users"
        }`,
        "error"
      );
    } finally {
      setUsersLoading(false);
    }
  }, [showToast, t]);

  const fetchSquads = useCallback(async () => {
    setSquadsLoading(true);
    try {
      // Fetch all squads
      const { data: squadsData, error: squadsError } = await supabase
        .from("squads")
        .select("*")
        .order("created_at", { ascending: false });

      if (squadsError) throw squadsError;
      if (!squadsData) return;

      const typedSquadsData = squadsData as SquadRow[];

      // Fetch creator profiles for all squads
      const ownerIds = [
        ...new Set(typedSquadsData.map((s) => s.owner_id).filter(Boolean)),
      ];

      const creatorsMap = new Map<string, CreatorInfo>();
      if (ownerIds.length > 0) {
        const { data: creatorsData, error: creatorsError } = await supabase
          .from("profiles")
          .select("id, name, email")
          .in("id", ownerIds);

        if (!creatorsError && creatorsData) {
          (creatorsData as Pick<ProfileRow, "id" | "name" | "email">[]).forEach(
            (creator) => {
              creatorsMap.set(creator.id, {
                name: creator.name,
                email: creator.email,
              });
            }
          );
        }
      }

      // Get member counts for each squad
      const squadsWithMembers: SquadForAdmin[] = await Promise.all(
        typedSquadsData.map(async (squad) => {
          const { count, error: countError } = await supabase
            .from("squad_members")
            .select("*", { count: "exact", head: true })
            .eq("squad_id", squad.id);

          if (countError) {
            console.error(
              `Failed to get member count for squad ${squad.id}:`,
              countError
            );
          }

          const creator = squad.owner_id
            ? creatorsMap.get(squad.owner_id)
            : null;

          return {
            ...squad,
            member_count: count ?? 0,
            creator_name: creator?.name ?? null,
            creator_email: creator?.email ?? null,
          };
        })
      );

      setSquads(squadsWithMembers);
    } catch (error) {
      console.error("Failed to fetch squads:", error);
      showToast(
        `${t("common.error")}: ${
          error instanceof Error ? error.message : "Failed to fetch squads"
        }`,
        "error"
      );
    } finally {
      setSquadsLoading(false);
    }
  }, [showToast, t]);

  useEffect(() => {
    fetchUsers();
    fetchSquads();
  }, [fetchUsers, fetchSquads]);

  const deleteSquad = useCallback(
    async (squadId: string, squadName: string) => {
      // Find the squad to check member count
      const squad = squads.find((s) => s.id === squadId);

      if (!squad) {
        showToast(t("admin.squadNotFound"), "error");
        return;
      }

      // Check if squad has members
      if (squad.member_count > 0) {
        showToast(t("admin.cannotDeleteSquadWithMembers"), "error");
        return;
      }

      if (
        !window.confirm(
          `${t("admin.confirmDeleteSquad")} "${squadName}"? ${t(
            "admin.deleteSquadWarning"
          )}`
        )
      ) {
        return;
      }

      setDeletingSquad(squadId);
      try {
        // Double-check member count before deletion
        const { count: memberCount, error: countError } = await supabase
          .from("squad_members")
          .select("*", { count: "exact", head: true })
          .eq("squad_id", squadId);

        if (countError) {
          throw new Error(
            `Failed to verify member count: ${countError.message}`
          );
        }

        if (memberCount && memberCount > 0) {
          showToast(t("admin.cannotDeleteSquadWithMembers"), "error");
          setDeletingSquad(null);
          return;
        }

        const { error } = await supabase
          .from("squads")
          .delete()
          .eq("id", squadId);

        if (error) {
          console.error("Delete error:", error);
          throw error;
        }

        showToast(t("admin.squadDeleted"), "success");
        await fetchSquads();
      } catch (error) {
        console.error("Failed to delete squad:", error);
        showToast(
          `${t("common.error")}: ${
            error instanceof Error
              ? error.message
              : t("admin.failedToDeleteSquad")
          }`,
          "error"
        );
      } finally {
        setDeletingSquad(null);
      }
    },
    [squads, showToast, t, fetchSquads]
  );

  const updateMatch = useCallback(
    async (matchId: string, updates: Partial<MatchForAdmin>) => {
      setSaving(matchId);

      // Build update object with only defined fields
      const updatePayload: {
        home_score?: number | null;
        away_score?: number | null;
        status?: "upcoming" | "live" | "completed";
      } = {};
      if (updates.home_score !== undefined)
        updatePayload.home_score = updates.home_score;
      if (updates.away_score !== undefined)
        updatePayload.away_score = updates.away_score;
      if (updates.status !== undefined) updatePayload.status = updates.status;

      // Type assertion needed due to Supabase type inference limitation with update method
      const updateQuery = supabase
        .from("matches")
        .update(updatePayload as never);
      const { error } = await updateQuery.eq("id", matchId);

      if (!error) {
        // Refresh matches to show updated data
        await fetchMatches();
        if (updates.status === "completed") {
          showToast(t("admin.matchCompleted"), "success");
        } else {
          showToast(t("admin.matchUpdated"), "success");
        }
      } else {
        showToast(`${t("common.error")}: ${error.message}`, "error");
      }

      setSaving(null);
    },
    [showToast, t, fetchMatches]
  );

  // Compute users with country flags when translations are available
  const usersWithFlags = useMemo(() => {
    return users.map((user) => {
      const countryFlag =
        user.supported_country && translations.has(user.supported_country)
          ? translations.get(user.supported_country)?.flag ?? null
          : null;
      return {
        ...user,
        country_flag: countryFlag,
      };
    });
  }, [users, translations]);

  // Filter matches by status (using original state from DB)
  const activeMatches = useMemo(() => {
    return matches.filter((m) => {
      const original = originalMatches.find((o) => o.id === m.id);
      return original && original.status !== "completed";
    });
  }, [matches, originalMatches]);

  const completedMatches = useMemo(() => {
    return matches.filter((m) => {
      const original = originalMatches.find((o) => o.id === m.id);
      return original && original.status === "completed";
    });
  }, [matches, originalMatches]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-5 h-5 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md shadow-sm border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-md mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => onNavigate("landing")}
            className="h-9 w-9 flex items-center justify-center hover:bg-slate-100 rounded-xl transition-smooth"
          >
            <ArrowLeft
              className={`w-5 h-5 text-slate-600 ${
                language === "ar" ? "rotate-180" : ""
              }`}
            />
          </button>
          <span className="text-slate-900 font-semibold">
            {t("admin.title")}
          </span>
          <div className="w-9 h-9" />
        </div>
      </header>

      <main className="max-w-md mx-auto px-2 py-2 space-y-3">
        {/* Tabs */}
        <div className="flex gap-1.5 bg-white rounded-lg p-1 border border-gray-200">
          <button
            onClick={() => setActiveTab("matches")}
            className={`flex-1 py-2 px-2 rounded-md text-xs font-medium transition-smooth ${
              activeTab === "matches"
                ? "bg-primary text-white"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            {t("admin.matches")}
          </button>
          <button
            onClick={() => setActiveTab("users")}
            className={`flex-1 py-2 px-2 rounded-md text-xs font-medium transition-smooth ${
              activeTab === "users"
                ? "bg-primary text-white"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            {t("admin.users")}
          </button>
          <button
            onClick={() => setActiveTab("squads")}
            className={`flex-1 py-2 px-2 rounded-md text-xs font-medium transition-smooth ${
              activeTab === "squads"
                ? "bg-primary text-white"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            {t("admin.squads")}
          </button>
        </div>

        {/* Matches Tab */}
        {activeTab === "matches" && (
          <>
            {/* Editable Matches (Upcoming & Live) */}
            <div className="space-y-2">
              <h2 className="text-sm font-semibold text-gray-700 px-1">
                {t("admin.activeMatches")}
              </h2>
              {activeMatches.length === 0 ? (
                <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
                  <p className="text-xs text-gray-500">
                    {t("admin.noActiveMatches")}
                  </p>
                </div>
              ) : (
                activeMatches.map((match) => {
                  const homeTeamName = getCountryName(match.home_country_code);
                  const awayTeamName = getCountryName(match.away_country_code);

                  return (
                    <div
                      key={match.id}
                      className="bg-white rounded-lg shadow-sm border border-gray-200 p-2.5"
                    >
                      {/* Match Header */}
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-1.5 flex-1 min-w-0">
                          <span className="text-base">{match.home_flag}</span>
                          <span className="text-xs font-medium truncate">
                            {homeTeamName}
                          </span>
                          <span className="text-xs text-gray-400">vs</span>
                          <span className="text-xs font-medium truncate">
                            {awayTeamName}
                          </span>
                          <span className="text-base">{match.away_flag}</span>
                        </div>
                        <span
                          className={`px-2 py-1 rounded text-[10px] font-medium flex-shrink-0 ${
                            match.status === "live"
                              ? "bg-red-100 text-red-700"
                              : match.status === "completed"
                              ? "bg-gray-100 text-gray-700"
                              : "bg-blue-100 text-blue-700"
                          }`}
                        >
                          {match.status === "live"
                            ? t("admin.status.live")
                            : match.status === "completed"
                            ? t("admin.status.completed")
                            : t("admin.status.upcoming")}
                        </span>
                      </div>

                      <div className="text-[10px] text-gray-500 mb-1.5">
                        {match.match_time}
                      </div>

                      {/* Score Inputs */}
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <div className="flex-1">
                          <input
                            type="number"
                            min="0"
                            value={match.home_score ?? ""}
                            onChange={(e) => {
                              const newMatches = matches.map((m) =>
                                m.id === match.id
                                  ? {
                                      ...m,
                                      home_score: e.target.value
                                        ? parseInt(e.target.value)
                                        : null,
                                    }
                                  : m
                              );
                              setMatches(newMatches);
                            }}
                            className="w-full border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary bg-white"
                            placeholder={homeTeamName}
                          />
                        </div>

                        <span className="text-xs text-gray-400">-</span>

                        <div className="flex-1">
                          <input
                            type="number"
                            min="0"
                            value={match.away_score ?? ""}
                            onChange={(e) => {
                              const newMatches = matches.map((m) =>
                                m.id === match.id
                                  ? {
                                      ...m,
                                      away_score: e.target.value
                                        ? parseInt(e.target.value)
                                        : null,
                                    }
                                  : m
                              );
                              setMatches(newMatches);
                            }}
                            className="w-full border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary bg-white"
                            placeholder={awayTeamName}
                          />
                        </div>
                      </div>

                      {/* Status & Save */}
                      <div className="flex items-center gap-1.5">
                        <div className="flex-1 relative">
                          <select
                            value={match.status}
                            onChange={(e) => {
                              const newStatus = e.target.value as
                                | "upcoming"
                                | "live"
                                | "completed";
                              const newMatches = matches.map((m) =>
                                m.id === match.id
                                  ? {
                                      ...m,
                                      status: newStatus,
                                    }
                                  : m
                              );
                              setMatches(newMatches);
                            }}
                            className="w-full border border-gray-300 rounded pl-2 pr-7 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary appearance-none bg-white"
                          >
                            <option value="upcoming">
                              {t("admin.status.upcoming")}
                            </option>
                            <option value="live">
                              {t("admin.status.live")}
                            </option>
                            <option value="completed">
                              {t("admin.status.completed")}
                            </option>
                          </select>
                          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                        </div>

                        <button
                          onClick={() =>
                            updateMatch(match.id, {
                              home_score: match.home_score,
                              away_score: match.away_score,
                              status: match.status,
                            })
                          }
                          disabled={saving === match.id}
                          className="px-2 py-1 bg-primary text-white rounded text-xs font-medium hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-smooth"
                        >
                          {saving === match.id
                            ? t("admin.saving")
                            : t("admin.save")}
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Completed Matches Summary (Read-Only) */}
            <div className="space-y-2">
              <h2 className="text-sm font-semibold text-gray-700 px-1">
                {t("admin.completedMatches")}
              </h2>
              {completedMatches.length === 0 ? (
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 text-center">
                  <p className="text-xs text-gray-500">
                    {t("admin.noCompletedMatches")}
                  </p>
                </div>
              ) : (
                completedMatches.map((match) => {
                  const homeTeamName = getCountryName(match.home_country_code);
                  const awayTeamName = getCountryName(match.away_country_code);

                  return (
                    <div
                      key={match.id}
                      className="bg-gray-50 rounded-lg border border-gray-200 p-2.5"
                    >
                      {/* Match Header */}
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-1.5 flex-1 min-w-0">
                          <span className="text-base">{match.home_flag}</span>
                          <span className="text-xs font-medium truncate">
                            {homeTeamName}
                          </span>
                          <span className="text-xs text-gray-400">vs</span>
                          <span className="text-xs font-medium truncate">
                            {awayTeamName}
                          </span>
                          <span className="text-base">{match.away_flag}</span>
                        </div>
                        <span className="px-2 py-1 rounded text-[10px] font-medium bg-green-100 text-green-700">
                          ✓ {t("admin.status.completed")}
                        </span>
                      </div>

                      <div className="text-[10px] text-gray-500 mb-2">
                        {match.match_time}
                      </div>

                      {/* Final Score Display */}
                      <div className="flex items-center py-2 bg-white rounded border border-gray-200">
                        <div className="flex-1 text-center">
                          <div className="text-xs text-gray-500 mb-1">
                            {homeTeamName}
                          </div>
                          <div className="text-2xl font-bold text-gray-800">
                            {match.home_score ?? "-"}
                          </div>
                        </div>
                        <div className="text-xl font-bold text-gray-400">-</div>
                        <div className="flex-1 text-center">
                          <div className="text-xs text-gray-500 mb-1">
                            {awayTeamName}
                          </div>
                          <div className="text-2xl font-bold text-gray-800">
                            {match.away_score ?? "-"}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <div className="space-y-2">
            <h2 className="text-sm font-semibold text-gray-700 px-1">
              {t("admin.allUsers")} ({usersWithFlags.length})
            </h2>
            {usersLoading ? (
              <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
                <Loader2 className="w-5 h-5 text-primary animate-spin mx-auto mb-2" />
                <p className="text-xs text-gray-500">{t("common.loading")}</p>
              </div>
            ) : usersWithFlags.length === 0 ? (
              <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
                <p className="text-xs text-gray-500">{t("admin.noUsers")}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {usersWithFlags.map((user) => (
                  <div
                    key={user.id}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 p-3"
                  >
                    {/* User Header */}
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {user.country_flag && (
                          <span className="text-lg flex-shrink-0">
                            {user.country_flag}
                          </span>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900 truncate">
                            {user.name}
                          </div>
                          <div className="text-xs text-gray-500 truncate">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* User Stats */}
                    <div className="grid grid-cols-2 gap-2 mt-2 pt-2 border-t border-gray-100">
                      <div>
                        <div className="text-[10px] text-gray-500 mb-0.5">
                          {t("admin.predictions")}
                        </div>
                        <div className="text-sm font-semibold text-gray-900">
                          {user.total_predictions || 0}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] text-gray-500 mb-0.5">
                          {t("admin.registered")}
                        </div>
                        <div className="text-xs text-gray-700">
                          {formatDate(user.created_at, language)}
                        </div>
                      </div>
                    </div>

                    {/* Squad Info */}
                    {user.squad && (
                      <div className="mt-2 pt-2 border-t border-gray-100">
                        <div className="text-[10px] text-gray-500 mb-0.5">
                          {t("admin.squad")}
                        </div>
                        <div className="text-xs font-medium text-gray-900">
                          {user.squad.name} ({user.squad.code})
                        </div>
                      </div>
                    )}
                    {!user.squad && user.current_squad_id && (
                      <div className="mt-2 pt-2 border-t border-gray-100">
                        <div className="text-[10px] text-gray-400 italic">
                          {t("admin.squadNotFound")}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Squads Tab */}
        {activeTab === "squads" && (
          <div className="space-y-2">
            <h2 className="text-sm font-semibold text-gray-700 px-1">
              {t("admin.allSquads")} ({squads.length})
            </h2>
            {squadsLoading ? (
              <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
                <Loader2 className="w-5 h-5 text-primary animate-spin mx-auto mb-2" />
                <p className="text-xs text-gray-500">{t("common.loading")}</p>
              </div>
            ) : squads.length === 0 ? (
              <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
                <p className="text-xs text-gray-500">{t("admin.noSquads")}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {squads.map((squad) => (
                  <div
                    key={squad.id}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 p-3"
                  >
                    {/* Squad Header */}
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-gray-900 truncate">
                          {squad.name}
                        </div>
                        <div className="text-xs text-gray-500 font-mono">
                          {squad.code}
                        </div>
                      </div>
                      {squad.member_count === 0 && (
                        <button
                          onClick={() => deleteSquad(squad.id, squad.name)}
                          disabled={deletingSquad === squad.id}
                          className="ml-2 p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
                          title={t("admin.deleteSquad")}
                        >
                          {deletingSquad === squad.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      )}
                    </div>

                    {/* Squad Stats */}
                    <div className="grid grid-cols-2 gap-2 mt-2 pt-2 border-t border-gray-100">
                      <div>
                        <div className="text-[10px] text-gray-500 mb-0.5">
                          {t("admin.members")}
                        </div>
                        <div className="text-sm font-semibold text-gray-900">
                          {squad.member_count}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] text-gray-500 mb-0.5">
                          {t("admin.created")}
                        </div>
                        <div className="text-xs text-gray-700">
                          {formatDate(squad.created_at, language)}
                        </div>
                      </div>
                    </div>

                    {/* Creator Info */}
                    {squad.creator_name && (
                      <div className="mt-2 pt-2 border-t border-gray-100">
                        <div className="text-[10px] text-gray-500 mb-0.5">
                          {t("admin.createdBy")}
                        </div>
                        <div className="text-xs font-medium text-gray-900">
                          {squad.creator_name}
                        </div>
                        {squad.creator_email && (
                          <div className="text-[10px] text-gray-500 truncate">
                            {squad.creator_email}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
